import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import RichTextEditor from './RichTextEditor';
import { X, ChevronLeft, ChevronRight, Check, User, Briefcase, Building2 } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { getApiUrl } from '../config/api';
import { getAuthToken } from '../utils/authToken';

// Prop filter function to prevent unknown props from being forwarded to DOM
const shouldForwardProp = (prop) => {
  // Filter out common props that shouldn't be forwarded to DOM
  const filteredProps = ['completed', 'active', 'isOpen', 'status', 'isActive', 'isSelected', 'progress', 'isPlaying', 'isLoading'];
  return !filteredProps.includes(prop);
};

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border: 1px solid #333;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #333;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  color: #888;
  transition: all 0.2s;

  &:hover {
    background: #333;
    color: #fff;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #888;
  margin: 0;
  font-size: 0.875rem;
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  gap: 0.5rem;
`;

const ProgressStep = styled.div`
  flex: 1;
  height: 4px;
  background: ${props => props.completed ? '#28a745' : props.active ? '#007bff' : '#333'};
  border-radius: 2px;
  transition: all 0.3s;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.completed ? '#28a745' : props.active ? '#007bff' : '#333'};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s;
`;

const ModalBody = styled.div`
  padding: 2rem;
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
  background: #1a1a1a;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #666;
  }
`;

const StepContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #fff;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #333;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #2d2d2d;
  color: #fff;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #888;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #333;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  background: #2d2d2d;
  color: #fff;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #888;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #666;
  }
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #333;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: #2d2d2d;

  &:hover {
    border-color: #007bff;
    background: #333;
  }

  input[type="checkbox"] {
    margin: 0;
  }
`;

const CheckboxLabel = styled.div`
  flex: 1;
`;

const CheckboxTitle = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 0.875rem;
`;

const CheckboxDescription = styled.div`
  color: #888;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Toggle = styled.button`
  position: relative;
  width: 48px;
  height: 24px;
  background: ${props => props.active ? '#007bff' : '#333'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: all 0.2s;
  }
`;

const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem;
  border-top: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #1a1a1a;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: #333;
  color: #fff;
  border: 1px solid #555;

  &:hover:not(:disabled) {
    background: #444;
  }
`;

const PrimaryButton = styled(Button)`
  background: #007bff;
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: #0056b3;
  }
`;

const SkipButton = styled(Button)`
  background: none;
  color: #888;
  border: none;
  padding: 0.5rem 1rem;

  &:hover {
    color: #fff;
    background: #333;
  }
`;

const StepIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #333;
  color: #007bff;
  margin-bottom: 1rem;
`;

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user, isLoggedIn } = useUser();
  const [onboardingData, setOnboardingData] = useState({
    basic_profile: {
      display_name: '',
      town: '',
      country: ''
    },
    professional_role: {
      professional_roles: []
    },
    studio_profile: {
      studio_name: '',
      studio_description: '',
      website: '',
      services_offered: []
    }
  });
  const [options, setOptions] = useState({
    professionalRoles: [],
    studioServices: []
  });

  // Use refs to track if fetches are in progress to prevent duplicate calls
  const fetchingUserProfileRef = useRef(false);
  const fetchingOnboardingOptionsRef = useRef(false);

  const steps = [
    {
      id: 'basic_profile',
      title: 'Basic Profile',
      subtitle: 'Tell us about yourself',
      icon: User
    },
    {
      id: 'professional_role',
      title: 'Professional Role',
      subtitle: 'What do you do?',
      icon: Briefcase
    },
    {
      id: 'studio_profile',
      title: 'Studio Profile',
      subtitle: 'About your studio',
      icon: Building2
    }
  ];

  const fetchOnboardingOptions = useCallback(async () => {
    if (fetchingOnboardingOptionsRef.current) {
      console.log('Skipping fetchOnboardingOptions: already fetching.');
      return;
    }
    
    if (!isLoggedIn || !user) return;
    
    fetchingOnboardingOptionsRef.current = true;
    
    try {
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      const response = await fetch('/api/onboarding/options', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setOptions(data.data);
      }
    } catch (error) {
      console.error('Error fetching onboarding options:', error);
    } finally {
      fetchingOnboardingOptionsRef.current = false;
    }
  }, [isLoggedIn, user]);

  const fetchUserProfile = useCallback(async () => {
    if (fetchingUserProfileRef.current) {
      console.log('Skipping fetchUserProfile: already fetching.');
      return;
    }
    
    if (!isLoggedIn || !user) return;
    
    fetchingUserProfileRef.current = true;
    
    try {
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      const response = await fetch(getApiUrl('/user-profiles/me'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        const profile = data.data;
        
        const newOnboardingData = {
          basic_profile: {
            display_name: profile.display_name || '',
            town: profile.town || '',
            country: profile.country || ''
          },
          professional_role: {
            professional_roles: profile.professional_roles || []
          },
          studio_profile: {
            studio_name: profile.studio_name || '',
            studio_description: profile.studio_description || '',
            website: profile.website || '',
            services_offered: profile.services_offered || []
          }
        };
        
        setOnboardingData(newOnboardingData);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      fetchingUserProfileRef.current = false;
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (isOpen && isLoggedIn && user) {
      console.log('🔍 Onboarding modal opened, fetching data...');
      fetchOnboardingOptions();
      fetchUserProfile();
    }
  }, [isOpen, isLoggedIn, user, fetchOnboardingOptions, fetchUserProfile]);

  const handleInputChange = (step, field, value) => {
    setOnboardingData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: value
      }
    }));
  };

  const handleCheckboxChange = (step, field, value, checked) => {
    setOnboardingData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: checked 
          ? [...(prev[step][field] || []), value]
          : (prev[step][field] || []).filter(item => item !== value)
      }
    }));
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      // Save current step data before moving to next step
      const currentStepId = steps[currentStep].id;
      const stepData = onboardingData[currentStepId];
      
      if (stepData && isLoggedIn && user) {
        try {
          // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
          console.log(`🔍 Saving step ${currentStepId} before moving to next step:`, stepData);
          await fetch('/api/onboarding/progress', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              step: currentStepId,
              data: stepData
            })
          });
        } catch (error) {
          console.error('Error saving step data:', error);
        }
      }
      
      setCurrentStep(currentStep + 1);
    } else {
      await handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    const currentStepId = steps[currentStep].id;
    setLoading(true);
    
    try {
      if (!isLoggedIn || !user) return;
      
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      await fetch('/api/onboarding/progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          step: currentStepId,
          skipStep: true
        })
      });

      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        await handleComplete();
      }
    } catch (error) {
      console.error('Error skipping step:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    
    try {
      if (!isLoggedIn || !user) return;
      
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      // Save all steps data
      for (const step of steps) {
        const stepData = onboardingData[step.id];
        if (stepData) {
          console.log(`🔍 Saving step ${step.id} with data:`, stepData);
          await fetch('/api/onboarding/progress', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              step: step.id,
              data: stepData
            })
          });
        }
      }

      onComplete();
      onClose();
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDontShowAgain = async () => {
    setLoading(true);
    
    try {
      if (!isLoggedIn || !user) return;
      
      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      const token = await getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      await fetch('/api/onboarding/dismiss', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      onClose();
    } catch (error) {
      console.error('Error dismissing onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const currentStepId = steps[currentStep].id;
    const stepData = onboardingData[currentStepId];
    
    // Debug: Log what the form fields are receiving
    console.log('🔍 Form fields receiving data for step:', currentStepId, stepData);

    switch (currentStepId) {
      case 'basic_profile':
        return (
          <StepContent>
            <FormGroup>
              <Label>Display Name</Label>
              <Input
                type="text"
                value={stepData.display_name || ''}
                onChange={(e) => handleInputChange('basic_profile', 'display_name', e.target.value)}
                placeholder="Enter your name"
              />
              {/* Debug: Show the actual value */}
              <div style={{fontSize: '10px', color: '#666'}}>Debug: "{stepData.display_name}"</div>
            </FormGroup>
            <FormGroup>
              <Label>Town/City</Label>
              <Input
                type="text"
                value={stepData.town}
                onChange={(e) => handleInputChange('basic_profile', 'town', e.target.value)}
                placeholder="e.g., Belfast, London, New York"
              />
            </FormGroup>
            <FormGroup>
              <Label>Country</Label>
              <Input
                type="text"
                value={stepData.country}
                onChange={(e) => handleInputChange('basic_profile', 'country', e.target.value)}
                placeholder="e.g., United Kingdom, United States, Canada"
              />
            </FormGroup>
          </StepContent>
        );

      case 'professional_role':
        return (
          <StepContent>
            <CheckboxGroup>
              {options.professionalRoles.map(role => (
                <CheckboxItem key={role.id}>
                  <input
                    type="checkbox"
                    checked={stepData.professional_roles.includes(role.name)}
                    onChange={(e) => handleCheckboxChange('professional_role', 'professional_roles', role.name, e.target.checked)}
                  />
                  <CheckboxLabel>
                    <CheckboxTitle>{role.name}</CheckboxTitle>
                    {role.description && (
                      <CheckboxDescription>{role.description}</CheckboxDescription>
                    )}
                  </CheckboxLabel>
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </StepContent>
        );

      case 'studio_profile':
        return (
          <StepContent>
            <FormGroup>
              <Label>Studio Name</Label>
              <Input
                type="text"
                value={stepData.studio_name || ''}
                onChange={(e) => handleInputChange('studio_profile', 'studio_name', e.target.value)}
                placeholder="Enter your studio name"
              />
              {/* Debug: Show the actual value */}
              <div style={{fontSize: '10px', color: '#666'}}>Debug: "{stepData.studio_name}"</div>
            </FormGroup>
            <FormGroup>
              <Label>Studio Description</Label>
              <RichTextEditor
                value={stepData.studio_description}
                onChange={(content) => handleInputChange('studio_profile', 'studio_description', content)}
                placeholder="Describe your studio, services, and what makes it unique..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Website (Optional)</Label>
              <Input
                type="url"
                value={stepData.website}
                onChange={(e) => handleInputChange('studio_profile', 'website', e.target.value)}
                placeholder="https://yourstudio.com"
              />
            </FormGroup>
            <FormGroup>
              <Label>Services Offered</Label>
              <CheckboxGroup>
                {options.studioServices.map(service => (
                  <CheckboxItem key={service.id}>
                    <input
                      type="checkbox"
                      checked={stepData.services_offered.includes(service.name)}
                      onChange={(e) => handleCheckboxChange('studio_profile', 'services_offered', service.name, e.target.checked)}
                    />
                    <CheckboxLabel>
                      <CheckboxTitle>{service.name}</CheckboxTitle>
                      {service.description && (
                        <CheckboxDescription>{service.description}</CheckboxDescription>
                      )}
                    </CheckboxLabel>
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            </FormGroup>
          </StepContent>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Debug: Log current onboarding data
  console.log('🔍 Current onboarding data state:', onboardingData);

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <AnimatePresence>
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
        <ModalContent
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
            <Title>Complete Your Profile</Title>
            <Subtitle>Help us personalize your experience</Subtitle>
            
            <ProgressBar>
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <StepIndicator
                    completed={index < currentStep}
                    active={index === currentStep}
                  >
                    {index < currentStep ? <Check size={16} /> : index + 1}
                  </StepIndicator>
                  {index < steps.length - 1 && (
                    <ProgressStep
                      completed={index < currentStep}
                      active={index === currentStep}
                    />
                  )}
                </React.Fragment>
              ))}
            </ProgressBar>
          </ModalHeader>

          <ModalBody>
            <motion.div
              key={currentStep}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <StepIcon>
                {React.createElement(steps[currentStep].icon, { size: 24 })}
              </StepIcon>
              <Title>{steps[currentStep].title}</Title>
              <Subtitle>{steps[currentStep].subtitle}</Subtitle>
              {renderStepContent()}
            </motion.div>
          </ModalBody>

          <ModalFooter>
            <div>
              {currentStep > 0 && (
                <SecondaryButton onClick={handlePrevious} disabled={loading}>
                  <ChevronLeft size={16} />
                  Previous
                </SecondaryButton>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <SkipButton onClick={handleSkip} disabled={loading}>
                Skip
              </SkipButton>
              <PrimaryButton onClick={handleNext} disabled={loading}>
                {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
                {currentStep < steps.length - 1 && <ChevronRight size={16} />}
              </PrimaryButton>
            </div>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
    </StyleSheetManager>
  );
};

export default OnboardingModal;
